const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';
const ZOOM_IN_SCALE_AMOUNT = 0.2 / 100;

let zoomAnimationObserver;
let zoomAnimationHandlerAttached = false;
const visibleZoomAnimationElements = new Set();

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

function updateZoomInRatio(elements = visibleZoomAnimationElements) {
  Array.from(elements).forEach((element) => {
    element.style.setProperty('--zoom-in-ratio', 1 + ZOOM_IN_SCALE_AMOUNT * percentageSeen(element));
  });
}

function onZoomAnimationIntersection(entries) {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      visibleZoomAnimationElements.add(entry.target);
    } else {
      visibleZoomAnimationElements.delete(entry.target);
    }
  });

  if (visibleZoomAnimationElements.size > 0) {
    updateZoomInRatio();
  }
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger(rootEl = document) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME)).filter(
    (element) => !element.hasAttribute('data-zoom-initialized')
  );

  if (animationTriggerElements.length === 0) return;

  if (!zoomAnimationObserver) {
    zoomAnimationObserver = new IntersectionObserver(onZoomAnimationIntersection);
  }

  animationTriggerElements.forEach((element) => {
    element.setAttribute('data-zoom-initialized', 'true');
    element.style.setProperty('--zoom-in-ratio', 1 + ZOOM_IN_SCALE_AMOUNT * percentageSeen(element));
    zoomAnimationObserver.observe(element);
  });

  if (!zoomAnimationHandlerAttached) {
    const handleZoomAnimation = throttle(() => {
      if (visibleZoomAnimationElements.size === 0) return;

      window.requestAnimationFrame(() => {
        updateZoomInRatio();
      });
    }, 50);

    window.addEventListener('scroll', handleZoomAnimation, { passive: true });
    window.addEventListener('resize', handleZoomAnimation, { passive: true });
    zoomAnimationHandlerAttached = true;
  }
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initializeScrollAnimationTrigger(event.target, true);
    initializeScrollZoomAnimationTrigger(event.target);
  });
  document.addEventListener('shopify:section:reorder', () => {
    initializeScrollAnimationTrigger(document, true);
    initializeScrollZoomAnimationTrigger(document);
  });
}
